import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoadingOverlay from "react-loading-overlay-ts";
import MUIDataTable from "mui-datatables";

// MUI Imports
import {
  Box,
  Card,
  CircularProgress,
  CardContent,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
// import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import { styled } from "@mui/material/styles";

// Project Imports
import Page from "../../../Components/Dashboard/Page";
import { PrintButtonIcon } from "../../../Components/Icons/DataTableIcons";
import {
  ViewIcon,
  MuiDataTableTheme,
} from "../../../Components/MuiDataTableTheme/MuiDataTableTheme";
import Searchbox from "../../../Components/Dashboard/Searchbox";
import { fShortenNumber } from "../../../Utils/formatNumber";
import Iconify from "../../../Components/Dashboard/Iconify";
import StatusGenerator from "../../../Components/StatusGenerator/StatusGenerator";
import FilterButton from "../../../Components/Button/FilterButton";
import { filterOptions } from "./utils.js";
import PrintLoanStatusFormat from "../../../Components/TrainerSetUp/PrintLoanStatusFormat";
import { DownloadIcon } from "./utils.js";

import {
  fetchStates,
  fetchTrainingCenterReport,
  exportTrainingCenterReport,
  resetExportTrainingCenterReport,
  printTrainingCenterReport,
  saveCurrentTrainerId,
} from "../../../Actions";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 5.5 + ITEM_PADDING_TOP,
      width: 200,
    },
  },
};

const RootStyle = styled(Card)(({ theme, bgcolor = "#FFE2E5" }) => ({
  boxShadow: "none",
  textAlign: "center",
  padding: theme.spacing(3, 0),
  color: theme.palette.primary.darker,
  backgroundColor: bgcolor,
  borderRadius: "16px",
}));

const IconWrapperStyle = styled("div")(({ theme, bgcolor = "#FA5A7D" }) => ({
  margin: "auto",
  display: "flex",
  borderRadius: "50%",
  alignItems: "center",
  width: theme.spacing(5),
  height: theme.spacing(5),
  justifyContent: "center",
  marginBottom: theme.spacing(2),
  color: "#fff",
  backgroundColor: bgcolor,
}));

const CardTotalPartners = ({
  value,
  title,
  bgcolor,
  iconColor,
  theIcon,
  id,
}) => {
  return (
    <RootStyle bgcolor={bgcolor} id={id}>
      <IconWrapperStyle bgcolor={iconColor}>
        <Iconify icon={theIcon} width={22} height={22} />
      </IconWrapperStyle>
      <Typography variant="h5" fontWeight={700} fontSize={"25px"}>
        {fShortenNumber(value)}
      </Typography>
      <Typography variant="subtitle2" sx={{ opacity: 0.72, fontSize: "12px" }}>
        {title}
      </Typography>
    </RootStyle>
  );
};

const ColumnTextStyle = {
  textTransform: "capitalize",
  textAlign: "left",
  color: "#212b36",
  fontSize: "0.875rem",
  fontWeight: 600,
};

const TrainersManagement = () => {
  const navigate = useNavigate();
  const globalDispatch = useDispatch();
  const trainersStore = (state) => state.trainersReducer;
  const ngaStatesStore = (state) => state.statesReducer;
  const {
    trainersReportData,
    exportingTrainersReport,
    printingTrainersReport,
    fetchingTrainersReportData,
    trainersReportDataNoPagination,
  } = useSelector(trainersStore);
  const { statesfetched } = useSelector(ngaStatesStore);

  const [trainersTableData, setTrainersTableData] = useState([]);
  const [getTrainersReportBody, setGetTrainersReportBody] = useState({
    pageNumber: 0,
    pageSize: 10,
    search: "",
    filterOptions: {
      State: "all",
    },
  });

  const dataReport = trainersReportData?.meta?.dataReport;

  const totalPending =
    dataReport?.totalSubmitted +
    dataReport?.totalStateOfficerApproved +
    dataReport?.totalStateCordinatorApproved +
    dataReport?.totalHeadQuatersApproved;

  const totalRejected =
    dataReport?.totalStateOfficerRejected +
    dataReport?.totalStateCordinatorRejected +
    dataReport?.totalHeadQuatersRejected +
    dataReport?.totalPhysicalVerificationRejected;

  const cards = [
    {
      title: "Total Training Centers",
      value: trainersReportData?.meta?.totalCount,
      icon: "null",
      bgcolor: "#C8E7FD",
      iconColor: "#0095FF",
      theIcon: "fluent:people-team-16-filled",
      id: "total-partners",
    },
    {
      title: "Total Pending",
      value: totalPending,
      icon: "null",
      bgcolor: "#ECE9CB",
      iconColor: "#BFAF25",
      theIcon: "fluent:people-team-delete-32-filled",
      id: "inactive-partners",
    },
    {
      title: "Total Fully Approved",
      value: dataReport?.totalPhysicalVerificationApproved,
      icon: "null",
      bgcolor: "#CBE1D7",
      iconColor: "#016C34",
      theIcon: "fluent:people-checkmark-16-filled",
      id: "active-partners",
    },
    {
      title: "Total Rejected",
      value: totalRejected,
      icon: "null",
      bgcolor: "#FFE2E5",
      iconColor: "#FA5A7D",
      theIcon: "fluent:people-checkmark-16-filled",
      id: "active-partners",
    },
  ];

  const trainersTableComponents = {
    icons: {
      DownloadIcon,
    },
  };

  const trainersTableOptions = {
    selectableRows: "none",
    jumpToPage: true,
    serverSide: true,
    count: trainersReportData?.meta?.totalCount,
    page: trainersReportData?.meta?.pageNumber - 1,
    rowsPerPage: 10,
    rowsPerPageOptions: [10],
    print: false,
    searchText: getTrainersReportBody.search,
    search: false,
    textLabels: {
      toolbar: {
        search: "Search",
        downloadCsv: "Download Excel Sheet",
      },
    },

    onDownload: () => {
      if (!exportingTrainersReport) {
        const { search, filterOptions } = getTrainersReportBody;
        const exportTrainersReportBody = { search, filterOptions };
        globalDispatch(exportTrainingCenterReport(exportTrainersReportBody));
      }
      return false;
    },
    customToolbar: () => {
      return (
        <>
          <Tooltip title="Print Trainers Report">
            <IconButton
              onClick={() => {
                const { search, filterOptions } = getTrainersReportBody;
                const printTrainersReportBody = { search, filterOptions };
                globalDispatch(
                  printTrainingCenterReport(printTrainersReportBody)
                );
              }}
            >
              {printingTrainersReport ? (
                <CircularProgress size={20} />
              ) : (
                <PrintButtonIcon />
              )}
            </IconButton>
          </Tooltip>
          <FilterButton
            options={filterOptions}
            filters={getTrainersReportBody.filterOptions}
            onClick={handleFilterClick}
            reset={handleReset}
          />
        </>
      );
    },

    onTableChange: (action, tableState) => {
      // treat tableState.page as index
      // there's a useEffect watching getTrainersReportBody
      if (action === "changePage") {
        setGetTrainersReportBody({
          ...getTrainersReportBody,
          pageNumber: tableState.page + 1,
        });
      }
    },
    disableRipple: true,
    filter: false,
    elevation: 0,
    filterType: "dropdown",
    responsive: "standard",
    fixedHeader: true,
    tableBodyMinHeight: "600px",
    tableBodyMaxHeight: "1000px",
  };

  const trainersTableColumns = [
    {
      name: "skillAcquisitionCentre",
      label: "Training Centre Name",
      options: {
        setCellProps: () => ({
          style: { minWidth: "190px", paddingLeft: "20px" },
        }),

        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              <span style={{ marginLeft: "20px" }}>{column.label}</span>
            </Typography>
          );
        },
        customBodyRender: (value) => {
          return value === undefined || value === "" || value === null
            ? "null"
            : value;
        },
        filter: false,
      },
    },
    {
      name: "state",
      label: "State",
      options: {
        setCellProps: () => ({ style: { minWidth: "100px" } }),
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
        customBodyRender: (value) => {
          return value === undefined || value === "" || value === null
            ? "null"
            : value;
        },
        filter: false,
      },
    },
    {
      name: "lga",
      label: "LGA",
      options: {
        setCellProps: () => ({ style: { minWidth: "100px" } }),
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
        customBodyRender: (value) => {
          return value === undefined || value === "" || value === null
            ? "null"
            : value;
        },
        filter: false,
      },
    },
    {
      name: "phoneNumber",
      label: "Phone Number",
      options: {
        setCellProps: () => ({ style: { minWidth: "120px" } }),
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
        customBodyRender: (value) => {
          return value === undefined || value === "" || value === null
            ? "-"
            : value;
        },
        filter: false,
      },
    },
    {
      name: "centerEmail",
      label: "Email Address",
      options: {
        setCellProps: () => ({
          style: { minWidth: "120px", padding: "0 15px" },
        }),
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        sort: false,
        setCellProps: () => ({ style: { minWidth: "200px" } }),
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
        customBodyRender: (value, tableMeta) => {
          switch (value) {
            case "Submitted":
              return (
                <StatusGenerator
                  statusText="Submitted"
                  variant="yellowFilled"
                />
              );
            case "StateOfficerApproved":
              return (
                <StatusGenerator
                  statusText="State Officer Approved"
                  variant="greenFilled"
                />
              );
            case "StateOfficerRejected":
              return (
                <StatusGenerator
                  statusText="State Officer Disapproved"
                  variant="redFilled"
                />
              );
            case "StateCordinatorApproved":
              return (
                <StatusGenerator
                  statusText="State Cord. Approved"
                  variant="greenFilled"
                />
              );
            case "StateCordinatorRejected":
              return (
                <StatusGenerator
                  statusText="State Cord. Disapproved"
                  variant="redFilled"
                />
              );
            case "HeadQuatersApproved":
              return (
                <StatusGenerator
                  statusText="HQ Approved"
                  variant="greenFilled"
                />
              );
            case "HeadQuatersRejected":
              return (
                <StatusGenerator
                  statusText="HQ Disapproved"
                  variant="redFilled"
                />
              );
            case "PhysicalVerificationApproved":
              return (
                <StatusGenerator
                  statusText="Physical Ver. Approved"
                  variant="greenFilled"
                />
              );
            case "PhysicalVerificationRejected":
              return (
                <StatusGenerator
                  statusText="Physical Ver. Disapproved"
                  variant="redFilled"
                />
              );
            default:
              return "";
          }
        },
        filter: true,
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        sort: false,
        filterType: false,
        filter: false,
        setCellProps: () => ({ style: { minWidth: "100px" } }),
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
        customBodyRender: (value, tableMeta) => {
          return (
            <>
              {value.approvalStatus === "StateCordinatorApproved" && (
                <Tooltip title={"View Trainer"}>
                  <IconButton
                    size="small"
                    onClick={() => handleViewTrainingCenter(value.id)}
                  >
                    <ViewIcon icon="material-symbols:visibility-outline" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        },
      },
    },
  ];

  function handleViewTrainingCenter(id) {
    navigate("/home/trainersmanagement/virtualApproval/approveTrainer");
    globalDispatch(saveCurrentTrainerId(id));
  }

  function handleSearchTextChange(event) {
    setGetTrainersReportBody({
      ...getTrainersReportBody,
      search: event.target.value,
    });
  }

  function handleFilterClick(event) {
    if (event !== 0) {
      setGetTrainersReportBody({
        ...getTrainersReportBody,
        filterOptions: {
          ...getTrainersReportBody.filterOptions,
          ...event,
        },
      });
    }
  }

  function handleReset() {
    setGetTrainersReportBody({
      ...getTrainersReportBody,
      filterOptions: {},
    });
  }

  function handleStateSelect(event) {
    console.log(event.target.value);
    setGetTrainersReportBody({
      ...getTrainersReportBody,
      filterOptions: {
        ...getTrainersReportBody.filterOptions,
        State: event.target.value,
      },
    });
  }

  useEffect(() => {
    globalDispatch(fetchTrainingCenterReport(getTrainersReportBody));
    globalDispatch(fetchStates());
  }, [getTrainersReportBody, globalDispatch]);

  useEffect(() => {
    if (trainersReportData?.data?.length > 0) {
      let tableData = [];

      trainersReportData.data.map((trainer) => {
        const tableRowData = {
          skillAcquisitionCentre: trainer.skillCenterName,
          state: trainer.state,
          lga: trainer.lga,
          phoneNumber: trainer.centerPhoneNumber,
          centerEmail: trainer.centerEmail,
          status: trainer.approvalStatus,
          actions: trainer,
        };

        tableData.push(tableRowData);
        return null;
      });

      setTrainersTableData(tableData);
    } else if (trainersReportData?.data?.length === 0) {
      setTrainersTableData([]);
    }
  }, [trainersReportData]);

  useEffect(() => {
    if (!exportingTrainersReport) {
      globalDispatch(resetExportTrainingCenterReport());
    }
  }, [exportingTrainersReport, globalDispatch]);

  useEffect(() => {
    if (trainersReportDataNoPagination?.length > 0) {
      var iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.srcdoc = document.getElementById("print-template").innerHTML;
      document.body.appendChild(iframe);
      iframe.contentWindow.print();
    }
  }, [trainersReportDataNoPagination]);

  return (
    <Page title="SAED | Trainers Management">
      <Container maxWidth="xl">
        <Box
          sx={{
            pb: 2,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Grid
            item
            xs
            style={{
              display: "flex",
              flexGrow: 4,
            }}
          >
            <Typography
              variant="h5"
              style={{ fontWeight: 600, color: "#212B36" }}
            >
              Trainers Management
            </Typography>
          </Grid>

          <Grid
            item
            xs
            style={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <FormControl fullWidth>
              <InputLabel>Select State</InputLabel>
              <Select
                size="small"
                label="Select State"
                MenuProps={MenuProps}
                onChange={(event) => handleStateSelect(event)}
                value={getTrainersReportBody.filterOptions.State}
              >
                <MenuItem value={"all"}>All States</MenuItem>
                {statesfetched?.length > 0 &&
                  statesfetched?.map((state) => (
                    <MenuItem key={state.stateID} value={state.stateID}>
                      {state.stateName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Box>

        <Box sx={{ pb: 2 }}>
          <Grid container spacing={3}>
            {cards.map((card) => {
              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md
                  key={`${card.title}-${card.value}`}
                >
                  <CardTotalPartners
                    value={card.value}
                    title={card.title}
                    bgcolor={card.bgcolor}
                    iconColor={card.iconColor}
                    theIcon={card.theIcon}
                    id={card.id}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box>
              <Paper
                sx={{
                  boxShadow: "0 1px 2px 0 rgb(145 158 171 / 24%)",
                  borderRadius: "20px",
                }}
              >
                <Card
                  elevation={0}
                  sx={{ padding: "0px !important", borderRadius: "20px" }}
                >
                  <CardContent sx={{ padding: "0px !important" }}>
                    <ThemeProvider theme={MuiDataTableTheme}>
                      <LoadingOverlay
                        active={fetchingTrainersReportData}
                        styles={{
                          overlay: (base) => ({
                            ...base,
                            background: "rgba(0, 0, 0, 0.15)",
                          }),
                        }}
                        spinner
                        text="Fetching Data for You..."
                      >
                        <MUIDataTable
                          title={
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={10}
                              justifyContent="space-between"
                            >
                              <Typography
                                sx={{ color: "#05004E", fontWeight: 600 }}
                              >
                                Training Centres
                              </Typography>
                              <Searchbox
                                placeholder="Training Center"
                                onChange={handleSearchTextChange}
                                value={getTrainersReportBody.search}
                              />
                            </Box>
                          }
                          data={trainersTableData}
                          columns={trainersTableColumns}
                          options={trainersTableOptions}
                          components={trainersTableComponents}
                          // displayData={displayData}
                          style={{ transition: "0.7s" }}
                        />
                      </LoadingOverlay>
                    </ThemeProvider>
                  </CardContent>
                </Card>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <div id="print-template" style={{ display: "none" }}>
        <PrintLoanStatusFormat data={trainersReportDataNoPagination} />
      </div>
    </Page>
  );
};

export default TrainersManagement;
